import React, { useState } from "react";
import { Menu, X, Megaphone, Instagram, Home, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { useNavigate } from "react-router-dom";

interface MobileHeaderProps {
  transparent?: boolean;
  className?: string;
  onNavigateToProperties?: () => void;
  onNavigateToAbout?: () => void;
  onAnnounceClick?: () => void;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  transparent = false,
  className,
  onNavigateToProperties,
  onNavigateToAbout,
  onAnnounceClick
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavigateToProperties = () => {
    navigate("/search-grid");
    onNavigateToProperties?.();
  };

  const handleNavigateToAbout = () => {
    // Se já estiver na home, faz scroll para a seção
    if (window.location.pathname === '/' || window.location.pathname === '') {
      const aboutSection = document.querySelector('#about-section');
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Se não estiver na home, navega para a home e depois faz scroll
      navigate('/');
      setTimeout(() => {
        const aboutSection = document.querySelector('#about-section');
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    onNavigateToAbout?.();
  };

  const navItems = [
    { label: "Imóveis", href: "#", icon: Home, onClick: handleNavigateToProperties },
    { label: "Sobre", href: "#", icon: Info, onClick: handleNavigateToAbout }
  ];

  return (
    <>
    <div className={cn(
      "bg-white box-border content-stretch flex items-center justify-between px-4 lg:px-10 py-0 relative w-full h-20",
      transparent && "bg-transparent",
      className
    )}>
      {/* Menu e Logo */}
      <div className="content-stretch flex gap-[24px] items-center relative shrink-0">
        <div className="relative shrink-0 size-[40px]">
          <button 
            onClick={toggleMenu}
            className="absolute inset-0 flex items-center justify-center"
            aria-label="Menu"
          >
            {isMenuOpen ? (
              <X className="w-[30px] h-[30px] text-gray-text" strokeWidth={1.5} />
            ) : (
              <Menu className="w-[30px] h-[30px] text-gray-text" strokeWidth={1.5} />
            )}
          </button>
        </div>
        <a href="/" className="flex items-center">
          <img 
            src="/logo-client.png" 
            alt="Logo" 
            className="h-[26px] w-auto object-contain"
          />
        </a>
      </div>

      {/* Botão Anuncie Aqui */}
      <button
        onClick={() => {
          navigate('/anuncie');
          onAnnounceClick?.();
        }}
        className="flex gap-1 lg:gap-2 h-10 lg:h-12 items-center justify-center px-3 lg:px-8 py-2 lg:py-4 rounded-[135px] border border-yellow-primary text-yellow-primary hover:bg-yellow-primary hover:text-white transition-all duration-200 group shrink-0"
      >
        <Megaphone className="w-4 h-4 lg:w-6 lg:h-6 text-yellow-primary group-hover:text-white transition-colors" />
        <span className="text-sm lg:text-xl font-normal text-yellow-primary group-hover:text-white leading-none whitespace-nowrap transition-colors">
          Anuncie aqui
        </span>
      </button>
    </div>

    {/* Mobile Menu Overlay */}
    {isMenuOpen && (
      <div className="fixed inset-0 z-50 lg:hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={toggleMenu}
        />

        {/* Menu Panel */}
        <div className="absolute left-0 top-0 h-full w-80 max-w-sm bg-white shadow-xl">
          <div className="flex h-20 items-center justify-between px-6 border-b">
            <img 
              src="/logo-client.png" 
              alt="Logo" 
              className="h-[26px] w-auto object-contain"
            />
            <button
              onClick={toggleMenu}
              className="p-2 -m-2"
              aria-label="Fechar menu"
            >
              <X className="w-6 h-6 text-gray-text" />
            </button>
          </div>

          <nav className="p-6">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      item.onClick?.();
                      toggleMenu();
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-text hover:bg-gray-50 hover:text-yellow-primary transition-colors"
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-base font-bold">{item.label}</span>
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-center gap-6">
                <span className="text-sm text-gray-500">Siga-nos:</span>
                <a
                  href="https://www.instagram.com/desordibroker?igsh=NHdwcGg2Ym9ocHZm"
                  target="_blank"
                  rel="noopener noreferrer"
                      className="text-gray-400 hover:text-yellow-primary transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <button
              onClick={() => {
                navigate('/anuncie');
                onAnnounceClick?.();
                toggleMenu();
              }}
              className="w-full flex gap-2 h-12 items-center justify-center px-6 py-4 rounded-[135px] border border-yellow-primary text-yellow-primary hover:bg-yellow-primary hover:text-white transition-all duration-200 group"
            >
              <Megaphone className="w-5 h-5 text-yellow-primary group-hover:text-white transition-colors" />
              <span className="text-base font-normal text-yellow-primary group-hover:text-white leading-none whitespace-nowrap transition-colors">
                Anuncie seu imóvel
              </span>
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export { MobileHeader, type MobileHeaderProps };
export default MobileHeader;